/**
 * Surprise Badge Configuration (Frontend)
 *
 * Defines engagement-based triggers for surprise badges.
 * Unlike report-count badges, these reward various forms of engagement.
 * The system is ONGOING - after initial milestones, badges continue at regular intervals.
 *
 * GATING: All surprise badges (except the first login badge) are gated behind
 * earning First Signal (submitting at least 1 report). This keeps new users
 * focused on the core action before introducing the engagement badge system.
 * The gating logic is implemented in achievementService.js on the backend.
 *
 * IMPORTANT: The core data (SURPRISE_TRIGGERS, ONGOING_TRIGGERS, SURPRISE_BADGE_TRIGGERS)
 * must stay in sync with the backend version at:
 * amplify/backend/function/monetaapp0f3f5572/src/lib/surpriseBadges.js
 *
 * When modifying triggers or thresholds, update BOTH files.
 * Run `npm test` to verify sync via jest/tests/surpriseBadgeSync.test.js
 */

import { t } from '../i18n';

/**
 * Trigger types for surprise badges
 * Each trigger tracks a specific engagement action
 */
export const SURPRISE_TRIGGERS = {
  // User actions
  LOGIN: 'login',
  REPORT_SUBMITTED: 'report_submitted',
  REPORT_VIEW: 'report_view',
  IMAGE_UPLOAD: 'image_upload',
  COMMENT_REPLY: 'comment_reply',
  BUG_SUBMITTED: 'bug_submitted',
  ANALYTICS_VIEWED: 'analytics_viewed',
  // Admin actions
  TAGS_ADDED: 'tags_added',
  REPORT_CLOSED: 'report_closed',
  REPORT_REOPENED: 'report_reopened',
  AI_OVERRIDDEN: 'ai_overridden',
  CSV_EXPORTED: 'csv_exported',
};

/**
 * Ongoing trigger configuration
 * After initial milestones, badges continue at these intervals
 * - startAfter: The threshold after which ongoing triggers begin
 * - interval: How often to award a badge (every Nth action)
 */
export const ONGOING_TRIGGERS = {
  // User actions
  [SURPRISE_TRIGGERS.LOGIN]: { startAfter: 100, interval: 25 },             // 125, 150, 175...
  [SURPRISE_TRIGGERS.REPORT_SUBMITTED]: { startAfter: 25, interval: 10 },   // 35, 45, 55... (starts after Culture Builder at 25)
  [SURPRISE_TRIGGERS.REPORT_VIEW]: { startAfter: 50, interval: 25 },        // 75, 100, 125...
  [SURPRISE_TRIGGERS.IMAGE_UPLOAD]: { startAfter: 50, interval: 25 },       // 75, 100, 125...
  [SURPRISE_TRIGGERS.COMMENT_REPLY]: { startAfter: 25, interval: 10 },      // 35, 45, 55...
  [SURPRISE_TRIGGERS.BUG_SUBMITTED]: { startAfter: 0, interval: 5 },        // 5, 10, 15...
  [SURPRISE_TRIGGERS.ANALYTICS_VIEWED]: { startAfter: 25, interval: 25 },   // 50, 75, 100...
  // Admin actions
  [SURPRISE_TRIGGERS.TAGS_ADDED]: { startAfter: 25, interval: 10 },         // 35, 45, 55...
  [SURPRISE_TRIGGERS.REPORT_CLOSED]: { startAfter: 25, interval: 10 },      // 35, 45, 55...
  [SURPRISE_TRIGGERS.REPORT_REOPENED]: { startAfter: 10, interval: 10 },    // 20, 30, 40...
  [SURPRISE_TRIGGERS.AI_OVERRIDDEN]: { startAfter: 10, interval: 10 },      // 20, 30, 40...
  [SURPRISE_TRIGGERS.CSV_EXPORTED]: { startAfter: 5, interval: 5 },         // 10, 15, 20...
};

/**
 * Badge trigger definitions
 * - type: The engagement action being tracked
 * - threshold: Number of actions needed to earn the badge
 * - description: What the user did to earn it
 *
 * First few are easy, then milestones at 5, then 10 after certain counts
 */
export const SURPRISE_BADGE_TRIGGERS = [
  // ===== VERY EASY - First actions =====
  { id: 'surprise_1', type: SURPRISE_TRIGGERS.LOGIN, threshold: 1, description: 'First login' },
  { id: 'surprise_2', type: SURPRISE_TRIGGERS.REPORT_VIEW, threshold: 1, description: 'First report viewed' },
  { id: 'surprise_3', type: SURPRISE_TRIGGERS.LOGIN, threshold: 3, description: 'Third login' },

  // ===== FIRST-TIME engagement actions =====
  // Note: REPORT_SUBMITTED milestones removed - they're covered by the main badge progression
  { id: 'surprise_4', type: SURPRISE_TRIGGERS.IMAGE_UPLOAD, threshold: 1, description: 'First image uploaded' },
  { id: 'surprise_5', type: SURPRISE_TRIGGERS.COMMENT_REPLY, threshold: 1, description: 'First comment reply' },
  { id: 'surprise_6', type: SURPRISE_TRIGGERS.TAGS_ADDED, threshold: 1, description: 'First tags added' },
  { id: 'surprise_22', type: SURPRISE_TRIGGERS.ANALYTICS_VIEWED, threshold: 1, description: 'First analytics view' },
  { id: 'surprise_23', type: SURPRISE_TRIGGERS.REPORT_CLOSED, threshold: 1, description: 'First report closed' },
  { id: 'surprise_24', type: SURPRISE_TRIGGERS.AI_OVERRIDDEN, threshold: 1, description: 'First AI override' },
  { id: 'surprise_25', type: SURPRISE_TRIGGERS.CSV_EXPORTED, threshold: 1, description: 'First CSV export' },

  // ===== EARLY milestones (5-10) =====
  { id: 'surprise_7', type: SURPRISE_TRIGGERS.IMAGE_UPLOAD, threshold: 5, description: '5 images uploaded' },
  { id: 'surprise_8', type: SURPRISE_TRIGGERS.LOGIN, threshold: 10, description: '10th login' },
  { id: 'surprise_9', type: SURPRISE_TRIGGERS.REPORT_VIEW, threshold: 10, description: '10 reports viewed' },
  { id: 'surprise_10', type: SURPRISE_TRIGGERS.COMMENT_REPLY, threshold: 5, description: '5 comment replies' },
  { id: 'surprise_27', type: SURPRISE_TRIGGERS.REPORT_CLOSED, threshold: 5, description: '5 reports closed' },
  { id: 'surprise_28', type: SURPRISE_TRIGGERS.TAGS_ADDED, threshold: 5, description: '5 tag additions' },
  { id: 'surprise_29', type: SURPRISE_TRIGGERS.ANALYTICS_VIEWED, threshold: 5, description: '5 analytics views' },
  { id: 'surprise_30', type: SURPRISE_TRIGGERS.AI_OVERRIDDEN, threshold: 5, description: '5 AI overrides' },
  { id: 'surprise_31', type: SURPRISE_TRIGGERS.CSV_EXPORTED, threshold: 5, description: '5 CSV exports' },

  // ===== MID milestones (10-25) =====
  { id: 'surprise_11', type: SURPRISE_TRIGGERS.IMAGE_UPLOAD, threshold: 10, description: '10 images uploaded' },
  { id: 'surprise_12', type: SURPRISE_TRIGGERS.LOGIN, threshold: 25, description: '25th login' },
  { id: 'surprise_13', type: SURPRISE_TRIGGERS.REPORT_VIEW, threshold: 25, description: '25 reports viewed' },
  { id: 'surprise_14', type: SURPRISE_TRIGGERS.COMMENT_REPLY, threshold: 10, description: '10 comment replies' },
  { id: 'surprise_15', type: SURPRISE_TRIGGERS.IMAGE_UPLOAD, threshold: 25, description: '25 images uploaded' },
  { id: 'surprise_33', type: SURPRISE_TRIGGERS.REPORT_CLOSED, threshold: 10, description: '10 reports closed' },
  { id: 'surprise_34', type: SURPRISE_TRIGGERS.TAGS_ADDED, threshold: 10, description: '10 tag additions' },
  { id: 'surprise_35', type: SURPRISE_TRIGGERS.REPORT_REOPENED, threshold: 5, description: '5 reports reopened' },
  { id: 'surprise_36', type: SURPRISE_TRIGGERS.AI_OVERRIDDEN, threshold: 10, description: '10 AI overrides' },
  { id: 'surprise_37', type: SURPRISE_TRIGGERS.ANALYTICS_VIEWED, threshold: 10, description: '10 analytics views' },
  { id: 'surprise_39', type: SURPRISE_TRIGGERS.REPORT_CLOSED, threshold: 25, description: '25 reports closed' },
  { id: 'surprise_40', type: SURPRISE_TRIGGERS.TAGS_ADDED, threshold: 25, description: '25 tag additions' },
  { id: 'surprise_41', type: SURPRISE_TRIGGERS.ANALYTICS_VIEWED, threshold: 25, description: '25 analytics views' },
  { id: 'surprise_42', type: SURPRISE_TRIGGERS.REPORT_REOPENED, threshold: 10, description: '10 reports reopened' },

  // ===== ADVANCED milestones (50-100) =====
  { id: 'surprise_16', type: SURPRISE_TRIGGERS.LOGIN, threshold: 50, description: '50th login' },
  { id: 'surprise_17', type: SURPRISE_TRIGGERS.REPORT_VIEW, threshold: 50, description: '50 reports viewed' },
  { id: 'surprise_18', type: SURPRISE_TRIGGERS.IMAGE_UPLOAD, threshold: 50, description: '50 images uploaded' },
  { id: 'surprise_19', type: SURPRISE_TRIGGERS.COMMENT_REPLY, threshold: 25, description: '25 comment replies' },
  { id: 'surprise_20', type: SURPRISE_TRIGGERS.LOGIN, threshold: 100, description: '100th login' },
];

/**
 * Encouraging messages shown when a surprise badge is earned
 * Rotates through these to keep things fresh
 * Focused on safety contribution and community building
 */
/**
 * Encouraging messages shown when a bonus badge is earned.
 *
 * A function rather than a constant so the copy resolves in the active
 * language at call time. The catalog holds the array under badges:encouraging.
 */
export function getEncouragingMessages() {
  return t('badges:encouraging', { returnObjects: true }) || [];
}

/**
 * Get a random encouraging message
 * @returns {string} A random message from the array
 */
export function getRandomEncouragingMessage() {
  const messages = getEncouragingMessages();
  if (messages.length === 0) return '';
  const index = Math.floor(Math.random() * messages.length);
  return messages[index];
}

/**
 * Get message for specific badge count (can customize for milestones)
 * @param {number} badgeCount - Current surprise badge count
 * @returns {string} Appropriate message
 */
export function getMessageForBadgeCount(badgeCount) {
  // Special messages for milestone counts
  const milestones = { 1: 'first', 5: 'five', 10: 'ten', 25: 'twentyFive', 50: 'fifty' };
  if (milestones[badgeCount]) {
    return t(`badges:milestone.${milestones[badgeCount]}`);
  }

  // Default to random message
  return getRandomEncouragingMessage();
}

/**
 * Calculate how many ongoing badges have been earned for a trigger type
 * @param {string} triggerType - The trigger type
 * @param {number} currentCount - Current count for this trigger
 * @returns {number} Number of ongoing badges earned
 */
export function getOngoingBadgeCount(triggerType, currentCount) {
  const config = ONGOING_TRIGGERS[triggerType];
  if (!config || currentCount <= config.startAfter) {
    return 0;
  }

  // Calculate how many intervals have been completed after startAfter
  const countAfterStart = currentCount - config.startAfter;
  return Math.floor(countAfterStart / config.interval);
}

/**
 * Check which badges would be earned given current engagement counts
 * Includes both initial triggers AND ongoing milestones
 * @param {Object} engagementCounts - Current counts for each trigger type
 * @param {Array} alreadyEarned - Array of badge IDs already earned
 * @returns {Array} Array of newly earned badge objects
 */
export function checkNewlyEarnedBadges(engagementCounts, alreadyEarned = []) {
  const earnedSet = new Set(alreadyEarned);
  const newlyEarned = [];

  // Check initial defined triggers
  for (const trigger of SURPRISE_BADGE_TRIGGERS) {
    if (earnedSet.has(trigger.id)) {
      continue; // Already earned
    }

    const currentCount = engagementCounts[trigger.type] || 0;
    if (currentCount >= trigger.threshold) {
      newlyEarned.push(trigger);
    }
  }

  return newlyEarned;
}

/**
 * Calculate total surprise badges earned (initial + ongoing)
 * @param {Object} engagementCounts - Current counts for each trigger type
 * @param {Array} earnedInitialBadges - Array of initial badge IDs earned
 * @returns {number} Total badges earned
 */
export function calculateTotalSurpriseBadges(engagementCounts, earnedInitialBadges = []) {
  // Count initial badges earned
  let total = earnedInitialBadges.length;

  // Add ongoing badges for each trigger type
  for (const triggerType of Object.keys(ONGOING_TRIGGERS)) {
    const count = engagementCounts[triggerType] || 0;
    total += getOngoingBadgeCount(triggerType, count);
  }

  return total;
}

/**
 * Get the number of initial (non-ongoing) surprise badges
 * @returns {number} Count of initial badges
 */
export function getInitialBadgeCount() {
  return SURPRISE_BADGE_TRIGGERS.length;
}

/**
 * Check if a specific ongoing milestone was just reached
 * @param {string} triggerType - The trigger type
 * @param {number} previousCount - Previous count
 * @param {number} currentCount - Current count
 * @returns {number} Number of NEW ongoing badges earned in this increment
 */
export function checkNewOngoingBadges(triggerType, previousCount, currentCount) {
  const prevOngoing = getOngoingBadgeCount(triggerType, previousCount);
  const currOngoing = getOngoingBadgeCount(triggerType, currentCount);
  return currOngoing - prevOngoing;
}
